<template>
  <ag-grid-vue ref="table" style="width: 100%; height: 100%" class="ag-theme-material" :pagination="true"
    :gridOptions="tableOptions"></ag-grid-vue>
</template>
<script setup="props">
/**
 * DataTableV2 — server-side (ag-grid Infinite Row Model) sibling of DataTable.
 *
 * Reuses the EXACT same custom row values (cell renderers, filters, value
 * formatters, comparators) as DataTable via the shared `createColumnTypes()`
 * factory, but fetches rows on demand from a `fetchRows` callback (e.g. an
 * Elasticsearch-backed search) instead of holding the whole dataset in memory.
 *
 * `fetchRows` contract:
 *   async ({ startRow, endRow, sortModel, filterModel, quickFilter }) =>
 *     { rows: object[], total?: number }
 *
 * Caveats (infinite row model):
 *  - Custom filter COMPONENTS still render, but their client-side
 *    `doesFilterPass` is bypassed; `fetchRows` receives the raw `filterModel`
 *    and must translate it server-side.
 *  - CSV export covers only the rows currently loaded into the cache.
 *  - `quickFilter` is not applied client-side; it is forwarded to `fetchRows`
 *    and the cache is refreshed when it changes.
 */
import { AgGridVue } from "ag-grid-vue3";
import { ref, defineProps, computed, watch, defineEmits, onBeforeUnmount } from "vue";
import { prepareTableGridOptions } from "./prepareTableGridOptions.js";
import { sanitizeColDefs } from "./sanitizeColDefs.js";
import { createColumnTypes } from "./tableColumnTypes.js";

// event emitters
const emit = defineEmits(["itemSelected", "optionsChanged", "gridReady", "dataError"]);

// reference for the table
const table = ref(null);
const tableApi = ref({});
const gridApi = ref({});
const columnApi = ref({});

const props = defineProps({
  // Required for server-side data: resolves a page of rows + optional total.
  fetchRows: Function,
  quickFilter: String,
  paginationPageSize: Number,
  tableSettings: Object,
  paginationAutoPageSize: Boolean,
  cacheBlockSize: Number,
  maxBlocksInCache: Number,
});

const tableSettingsWithColumnStateRemoved = computed(() => {
  let tableSettings = { ...props.tableSettings };
  delete tableSettings.columnState;
  return tableSettings;
});

// Latest quick-filter term, forwarded to fetchRows (infinite model does not
// support client-side quick filtering).
const quickFilterTerm = ref(props.quickFilter || "");

const buildDatasource = () => ({
  getRows: async (params) => {
    if (typeof props.fetchRows !== "function") {
      params.successCallback([], 0);
      return;
    }
    try {
      const result = await props.fetchRows({
        startRow: params.startRow,
        endRow: params.endRow,
        sortModel: params.sortModel,
        filterModel: params.filterModel,
        quickFilter: quickFilterTerm.value,
      });
      const rows = Array.isArray(result?.rows) ? result.rows : [];
      const total = typeof result?.total === "number" ? result.total : -1;
      params.successCallback(rows, total);
    } catch (error) {
      console.error("DataTableV2 fetchRows error:", error);
      emit("dataError", error);
      params.failCallback();
    }
  },
});

const tableOptions = ref(prepareTableGridOptions({
  rowModelType: "infinite",
  cacheBlockSize: props.cacheBlockSize || props.paginationPageSize || 25,
  maxBlocksInCache: props.maxBlocksInCache || 10,
  onRowClicked(row) {
    emit("itemSelected", row);
  },
  onGridReady(params) {
    tableApi.value = params.api;
    columnApi.value = params.api;
    gridApi.value = params?.api?.filterManager?.gridOptionsService?.api || params?.api?.filterManager?.gos?.api;

    setTableSettings();
    params.api.setGridOption("datasource", buildDatasource());
    emit("gridReady", params.api);
  },
  getRowStyle(params) {
    if (params.node.rowIndex % 2 === 1) {
      return { background: "#eee" };
    }
  },
  onFilterChanged() {
    changeMade();
  },
  onSortChanged() {
    changeMade();
  },
  onColumnVisible() {
    changeMade();
  },
  onColumnPinned() {
    changeMade();
  },
  onColumnResized() {
    changeMade();
  },
  onColumnMoved() {
    changeMade();
  },
  onColumnValueChanged() {
    changeMade();
  },
  onPaginationChanged() {
    changeMade();
  },
  defaultColDef: {
    sortable: true,
    filter: true,
    resizable: true,
    cellDataType: false,
    minWidth: 100,
    suppressFieldDotNotation: false,
    useValueFormatterForExport: true,
    useValueParserForImport: true,
    enableFilterHandlers: true,
  },
  rowHeight: 36,
  enableFilterHandlers: true,
  paginationPageSize: props.paginationPageSize || 25,
  paginationAutoPageSize: props.paginationAutoPageSize || false,
  suppressFieldDotNotation: false,
  columnDefs: [...(props?.tableSettings?.columnState || [])],
  filterModel: props?.tableSettings?.filter || {},
  columnTypes: createColumnTypes(),
  ...tableSettingsWithColumnStateRemoved.value,
}));

const setTableSettings = () => {
  
  if (props?.tableSettings?.filter && tableApi.value?.setFilterModel) {
    tableApi.value.setFilterModel(props?.tableSettings?.filter);
  }
  if (typeof props?.tableSettings?.page === "number" && tableApi.value?.paginationGoToPage) {
    tableApi.value.paginationGoToPage(props.tableSettings.page);
  }
};

const refresh = () => {
  if (tableApi.value?.refreshInfiniteCache) {
    tableApi.value.refreshInfiniteCache();
  }
};

const purge = () => {
  if (tableApi.value?.purgeInfiniteCache) {
    tableApi.value.purgeInfiniteCache();
  }
};

let quickFilterTimeout;
watch(
  () => props.quickFilter,
  (newValue) => {
    quickFilterTerm.value = newValue || "";
    clearTimeout(quickFilterTimeout);
    quickFilterTimeout = setTimeout(() => {
      purge();
    }, 250);
  }
);

watch(
  () => props.tableSettings?.columnState,
  (newColumnState) => {
    if (tableApi.value?.setGridOption && newColumnState?.length > 0) {
      tableApi.value.setGridOption("columnDefs", sanitizeColDefs(newColumnState));
    }
  },
  { deep: true }
);

const destroy = () => {
  try {
    const api = tableApi.value;
    tableApi.value = null;
    columnApi.value = null;
    gridApi.value = null;
    api?.destroy?.();
  } catch (error) {
    // no-op
  }
};

const download = () => {
  props.tableSettings?.exportParams
    ? tableApi.value.exportDataAsCsv(props.tableSettings.exportParams)
    : tableApi.value.exportDataAsCsv();
};

var to;
const changeMade = () => {
  clearTimeout(to);
  to = setTimeout(() => {
    emit("optionsChanged");
  }, 100);
};

onBeforeUnmount(() => {
  destroy();
});

defineExpose({
  download,
  refresh,
  purge,
  tableApi,
  columnApi,
  table,
  setTableSettings,
});
</script>
